/**
 * Which step of character creation you are on, and what is stopping you.
 *
 * **Pure, and separate from the page for the reason `selectedActionOf` and
 * `pointFromClick` are**: a wizard's bug and its fix produce identical markup.
 * A step that is silently never satisfiable, or one that unblocks before its
 * choice is legal, renders exactly like a working one — `renderToStaticMarkup`
 * cannot see either, and a browser only shows it to whoever presses the button.
 *
 * ⚠ **The step list is the constraint list.** Every choice here narrows the
 * next — an alignment depends on the class, and the scores are qualified
 * against the race and class already chosen — so each step carries both what
 * was chosen (kept visible in the list while you work on the next one) and, when
 * it is unfinished, the reason in the words of the rule that stopped you.
 * Nothing here decides anything: the server checks the whole character again on
 * arrival, and a greyed option is a courtesy.
 *
 * **Race and class come first, scores after.** The book's own chapter order
 * rolls scores before either, and this application used to follow it — but a
 * player choosing a *character* wants to say who they are before the dice
 * say what they got. The two qualification checks still run
 * in the two directions the book prints (a race against the scores **as
 * rolled**, a class against them **after** the racial adjustment); only the
 * order they are asked in is this application's, not the book's, and nothing
 * downstream of the scores step changed at all.
 */

import type { WizardStep } from '../pages/Wizard.js';
import {
  adjustedScores,
  bonusLanguageBudget,
  bonusLanguagePoolFor,
  classBlockers,
  optionLabel,
  raceBlockers,
  type CharacterOptions,
  type Scores,
} from './options.js';

/**
 * Everything the wizard has collected. One flat object rather than a step per
 * field, so the review step and the POST read the same thing the steps wrote.
 */
export interface CharacterDraft {
  readonly method: string;
  readonly rolled: Scores | null;
  /**
   * The points method's budget, once rolled — `null` for every other method,
   * and for this one too until "Roll" has been pressed. Kept apart from
   * `rolled`, which is the *spend*, so the allocation UI has something to
   * show remaining against.
   */
  readonly pointsBudget: number | null;
  /**
   * Exceptional Strength, as a percentage — the points method's own extra
   * spend, "each 10% of exceptional strength costs 1 point", only reachable
   * with a rolled or allocated Strength of exactly 18. Text, like the age and
   * body fields, because an empty box and a box holding 0 are different
   * things to a player and the same thing to `wholeNumber`.
   */
  readonly exceptionalStrength: string;
  readonly race: string;
  readonly characterClass: string;
  readonly specialistSchool: string;
  /** A mage's kind of book; ignored for anyone else. Standard unless they choose. */
  readonly spellbookType: string;
  /**
   * Beyond what the race grants free at start — `bonusLanguagesKnown`
   * (Intelligence, as rolled and racially adjusted) sets the budget,
   * `bonusLanguagePool` (race) sets which languages are pickable.
   */
  readonly bonusLanguages: readonly string[];
  readonly alignment: string;
  readonly name: string;
  /**
   * What the character is, in the player's words.
   *
   * Free text. `bodyColumn` below is the *other* question — which of Table
   * 2.5's two printed rows the height and weight were rolled off — and they are
   * separate fields because the book prints two columns of figures and a person
   * is not obliged to be one of them.
   */
  readonly gender: string;
  readonly bodyColumn: string;
  readonly age: string;
  readonly heightIn: string;
  readonly weightLb: string;
  readonly description: string;
  /**
   * What the character trained with, and how.
   *
   * ⚠ **Combat Skill Points had no surface at all before 2026-08-30**, so every
   * character made here spent none — and the book is explicit that whatever is
   * left at 1st level is *lost*. `tier` says which currency `name` is in: a
   * weapon key, one of Table 5.2's groups, or a damage type.
   */
  readonly weaponSkills: readonly { readonly name: string; readonly tier: string }[];
  /** At most one for almost everybody; see the three printed sentences. */
  readonly combatMethods: readonly { readonly name: string; readonly category: string }[];
  readonly ambidextrous: boolean;
  /** Single-class fighters only, and only in a weapon already skilled with. */
  readonly specialisedIn: string;
}

export const EMPTY_DRAFT: CharacterDraft = {
  method: '',
  rolled: null,
  pointsBudget: null,
  exceptionalStrength: '',
  race: '',
  characterClass: '',
  specialistSchool: '',
  spellbookType: 'standard',
  bonusLanguages: [],
  alignment: '',
  name: '',
  gender: '',
  bodyColumn: '',
  age: '',
  heightIn: '',
  weightLb: '',
  description: '',
  weaponSkills: [],
  combatMethods: [],
  ambidextrous: false,
  specialisedIn: '',
};

/** A measurement typed into a box: a whole number, or nothing. */
export function wholeNumber(text: string): number | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  const value = Number(trimmed);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

/**
 * The steps, in order, each with its state.
 *
 * ⚠ **A specialist mage's school is part of the class step, not a step of its
 * own.** The bonus slot the class grants is usable only for that school, so a
 * specialist with none silently loses it — which makes the school a condition
 * of having chosen the class at all rather than an extra question afterwards.
 */
export function characterSteps(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): WizardStep[] {
  const rolled = draft.rolled;
  const pointsUntouched = isPointsAllocationUntouched(draft, options);
  const classAlignment = options?.classes.find((c) => c.class === draft.characterClass)?.alignment;
  const age = wholeNumber(draft.age);
  const height = wholeNumber(draft.heightIn);
  const weight = wholeNumber(draft.weightLb);
  /**
   * ⚠ **A typed age used to reach Review clean and fail only at "Create
   * character."** A gnome typed as 34 against Table 2.6's starting age of 60
   * passed every step on the way and was refused only by the server, at the
   * very end. `done` on the body step checked only that a number was typed,
   * never that it was in the table's
   * own bounds — the one field of the four with a printed range at all. Read
   * off `options.bodies`, the same object the roll button already reads, so
   * this can never disagree with the server's own refusal. The message is the
   * server's wording verbatim, not a paraphrase, for exactly that reason.
   */
  const raceAges = options?.bodies?.find((one) => one.race === draft.race)?.age;
  const ageOutOfRange =
    age !== undefined && raceAges !== undefined
      ? age < raceAges.startingAge
        ? `Table 2.6 gives a ${draft.race} a starting age of ${raceAges.startingAge}; ${age} is younger than the book lets one adventure`
        : age > raceAges.maxAgeFormula.max
          ? `Table 2.6 gives a ${draft.race} a maximum age of ${raceAges.maxAgeFormula.printed}, so ${raceAges.maxAgeFormula.max} is the oldest one can be; ${age} is not`
          : ''
      : '';

  return [
    {
      // ⚠ **Race and class share one step.** Class's own list is filtered
      // by whichever race is picked (a race can forbid a class outright), so having them on
      // separate screens meant flipping back and forth to see the effect of
      // a choice on the very next one — exactly the back-and-forth a wizard
      // exists to remove.
      key: 'raceClass',
      title: 'Race and class',
      done:
        draft.race !== '' &&
        draft.characterClass !== '' &&
        (draft.characterClass !== 'specialistMage' || draft.specialistSchool !== ''),
      // ⚠ **No `blockedBy` for the plain case, on purpose.** Every race is a
      // live, enabled button with nothing else on screen — the design principle a blocked
      // step names its rule exists for a control that is dead with no
      // visible cause, and a row of clickable options is not that. The
      // specialist-school follow-up IS non-obvious — a class is picked, the
      // extra slot is silently lost, and nothing else on screen says so.
      ...(draft.characterClass === 'specialistMage' && draft.specialistSchool === ''
        ? { blockedBy: 'a school to specialise in — the extra slot is only good for one' }
        : {}),
    },
    {
      key: 'alignment',
      title: 'Alignment',
      done: draft.alignment !== '',
      // ⚠ **Only the genuinely non-obvious case gets a "Still needed" —
      // the unconstrained one ("an alignment") was exactly as trivial as
      // the old "a race", and dropped for the same reason.** The class's
      // own restriction is real information no button list shows on its
      // own, so it stays — read back from the server rather than restated,
      // the same rule that print it in the step body too.
      ...(classAlignment && classAlignment !== 'any'
        ? { blockedBy: `an alignment this class allows — the book says ${classAlignment}` }
        : {}),
    },
    {
      key: 'scores',
      title: 'Ability scores',
      // ⚠ **Qualified against the race and class already chosen, not the
      // other way round.** `scoreBlockers` asks the same two functions the
      // old order asked — a race against the roll `as rolled`, a class
      // against it after the racial adjustment — just against a fixed
      // target instead of narrowing a list of buttons.
      done: rolled !== null && !pointsUntouched && scoreBlockers(draft, options).length === 0,
      blockedBy:
        rolled === null
          ? 'a roll, or points allocated to a legal spread'
          : pointsUntouched
            ? `${draft.pointsBudget} points to allocate`
            : scoreBlockers(draft, options).join('; '),
    },
    {
      // ⚠ **Optional, like Combat training** — a budget of 0 is the common
      // case (most scores grant none at all) and forcing a step nobody can
      // act on is worse than a step that quietly has nothing to do. Read off
      // the *racially* adjusted score, the same reasoning `scoreBlockers`
      // already applies: the sheet stores what the character IS. Aging is
      // chosen later, on the Body step, and this deliberately does not wait
      // for it — a score aging would raise is undercounted here, never
      // overcounted, which is the same cautious direction `readsLanguage`
      // itself takes (harder to read, never easier). The server computes the
      // final figure off the fully adjusted score and is the one that decides.
      key: 'languages',
      title: 'Bonus languages',
      optional: true,
      done: bonusLanguageBudgetFor(draft, options) === 0 || draft.bonusLanguages.length > 0,
    },
    {
      key: 'name',
      title: 'Name',
      done: draft.name.trim() !== '',
      // One empty text box; "Still needed: a name" told you nothing the box
      // itself did not.
    },
    {
      key: 'body',
      title: 'Gender, age and build',
      /**
       * ⚠ **Required, and this is the step the sheet used to have nothing at
       * all for.** With none of it recorded the DM invented all four, freshly,
       * on every beat: a human mage was given fur and antennae, and her sex
       * changed between consecutive beats of one scene. One press of Roll
       * fills three of them, so the cost of the requirement is a click and the
       * cost of skipping it is paid for the life of the character.
       */
      done:
        draft.gender.trim() !== '' &&
        age !== undefined &&
        height !== undefined &&
        weight !== undefined &&
        ageOutOfRange === '',
      blockedBy: [
        draft.gender.trim() === '' ? 'a gender' : '',
        age === undefined ? 'an age' : ageOutOfRange,
        height === undefined || weight === undefined ? 'a height and weight' : '',
      ]
        .filter(Boolean)
        .join(', '),
    },
    {
      key: 'combat',
      title: 'Combat training',
      /**
       * ⚠ **Optional, on the same footing as Appearance, and for the reason a
       * live drive-through found**: `done: true` unconditionally put a
       * checkmark and a strikethrough on this step in the list from the very
       * first paint — before anything had been bought — while the step's own
       * body was showing an amber "N points will be lost" warning underneath
       * it. The header said "finished, move on"; the content said "you have
       * not done this". `optional: true` is what actually implements "never
       * blocked" (see `canLeave`), so `done` is free to mean what it means
       * everywhere else in this list: has something happened here.
       */
      optional: true,
      done: combatPointsSpent(draft, options) > 0,
    },
    {
      key: 'appearance',
      title: 'Appearance',
      // The one step you may pass with nothing done — the DM writes one when
      // it is left blank, once, and it is then fixed.
      optional: true,
      done: draft.description.trim() !== '',
    },
    {
      key: 'review',
      title: 'Review',
      /**
       * ⚠ **The answer to the standing objection to wizards**: that hiding the
       * constraints behind Next buttons produces a refusal at the end with
       * nothing to say which step was wrong. Nothing is hidden at the end —
       * the whole character is on screen before the button that makes it.
       */
      done: true,
    },
  ];
}

/**
 * Whether a points allocation exists and nothing has been spent from it yet.
 *
 * ⚠ **A fresh points allocation starts every score at the floor, and
 * qualification is checked against whatever `rolled` currently holds** — so
 * the instant "Roll budget" is pressed, before the player has touched a
 * single score, `scoreBlockers` sees six scores at the floor and reports
 * every race/class minimum as failed at once — reading as the points method
 * being broken, not as an invitation to spend. Shared
 * by the step's own `blockedBy` and by the points panel's inline "Does not
 * qualify" message — both used to ask this and only one was fixed at first,
 * which is exactly the two-places-answer-one-question trap this file warns
 * about elsewhere. Distinct from "no scores at all" (`rolled === null`) and
 * from "started spending, still short" (real feedback, unaffected).
 */
export function isPointsAllocationUntouched(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): boolean {
  const method = options?.generationMethods.find((m) => m.key === draft.method);
  return (
    method?.kind === 'points' && draft.pointsBudget !== null && abilityPointsSpent(draft, options) === 0
  );
}

/**
 * Why the roll or allocation on the scores step does not qualify for the
 * race and class already chosen — empty once it does.
 *
 * The two checks still run in the two directions the book prints: a race
 * against the scores **as rolled**, a class against them **after** the
 * racial adjustment. Only the order they are asked in has changed — this
 * asks both against a fixed target instead of using them to grey out a list
 * of buttons, because the target was picked before the dice were thrown.
 */
export function scoreBlockers(draft: CharacterDraft, options: CharacterOptions | null): string[] {
  if (!draft.rolled) return [];
  const raceOption = options?.races.find((r) => r.race === draft.race);
  const classOption = options?.classes.find((c) => c.class === draft.characterClass);
  const adjusted = adjustedScores(draft.rolled, raceOption);
  return [
    ...(raceOption ? raceBlockers(raceOption, draft.rolled) : []),
    ...(classOption ? classBlockers(classOption, raceOption, adjusted) : []),
  ];
}

/**
 * How many bonus languages this draft's Intelligence grants, racially
 * adjusted like every other qualification check in this file.
 *
 * See the `languages` step's own comment for why age is deliberately not
 * folded in here too.
 */
export function bonusLanguageBudgetFor(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): number {
  if (!draft.rolled) return 0;
  const raceOption = options?.races.find((r) => r.race === draft.race);
  const adjusted = adjustedScores(draft.rolled, raceOption);
  const intelligence = adjusted['int'];
  if (intelligence === undefined) return 0;
  return bonusLanguageBudget(options ?? undefined, intelligence);
}

/** Which languages this draft's race may spend a bonus pick on. */
export function bonusLanguagesPickable(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): string[] {
  return bonusLanguagePoolFor(options ?? undefined, draft.race);
}

/**
 * What the points method's budget has bought so far.
 *
 * "60 + 4d4 points to distribute (min 3, max 18 per score); each 10% of
 * exceptional strength costs 1 point" — the floor and the exceptional-strength
 * rate are read off the generation method actually in the draft, never
 * written down here, because a different printed floor would silently spend
 * against the wrong one.
 */
export function abilityPointsSpent(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): number {
  const method = options?.generationMethods.find((m) => m.key === draft.method);
  // Meaningless for a method that rolls — there is no floor to spend up
  // from, so a roll-kind method spends nothing rather than a number that
  // looks like one.
  if (!method?.pointsBounds) return 0;
  const floor = method.pointsBounds.min;
  let spent = 0;
  for (const ability of options?.abilities ?? []) {
    spent += (draft.rolled?.[ability] ?? floor) - floor;
  }
  const percent = wholeNumber(draft.exceptionalStrength);
  const rate = method.exceptionalStrengthPercentPerPoint;
  if (percent !== undefined && rate) {
    spent += Math.ceil(percent / rate);
  }
  return spent;
}

/** What is left of the points budget — negative once overspent. */
export function abilityPointsRemaining(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): number {
  return (draft.pointsBudget ?? 0) - abilityPointsSpent(draft, options);
}

/**
 * The six scores a fresh points allocation starts from — every ability at
 * the method's own printed floor, nothing spent yet.
 */
export function pointsFloorScores(options: CharacterOptions, method: { pointsBounds?: { min: number } }): Scores {
  const floor = method.pointsBounds?.min ?? 0;
  const out: Scores = {};
  for (const ability of options.abilities) out[ability] = floor;
  return out;
}

/**
 * The height and weight a roll would start from, for one race and column.
 *
 * Shown *before* the press so the roll is not a number out of nowhere, and read
 * from what the server published — Table 2.5 is the book's and a copy here
 * would be inventing a body.
 */
export function bodyRange(
  options: CharacterOptions | null,
  race: string,
  column: string,
): { height: string; weight: string } | undefined {
  const body = options?.bodies?.find((one) => one.race === race);
  const base = body?.heightWeight.baseHeightIn[column];
  const weightBase = body?.heightWeight.baseWeightLb[column];
  if (!body || base === undefined || weightBase === undefined) return undefined;
  const { heightModifier, weightModifier } = body.heightWeight;
  return {
    height: `${base + heightModifier.count}–${base + heightModifier.count * heightModifier.sides} in`,
    weight: `${weightBase + weightModifier.count}–${
      weightBase + weightModifier.count * weightModifier.sides
    } lb`,
  };
}

/**
 * What an age costs, if it costs anything.
 *
 * ⚠ **Cumulative** — every step at or below the age applies, which the three
 * printed lines do not repeat and the sentence above them states once. Shown
 * before the character is made, because the alternative is a player discovering
 * that their Strength is four lower than the number they rolled and having
 * nothing on screen that says why.
 */
export function agingNotice(
  options: CharacterOptions | null,
  race: string,
  age: number | undefined,
): string | undefined {
  const table = options?.bodies?.find((one) => one.race === race)?.age;
  if (!table || age === undefined) return undefined;
  const reached = table.steps.filter((step) => age >= step.beginsAt);
  if (reached.length === 0) return undefined;
  const totals: Record<string, number> = {};
  for (const step of reached) {
    for (const [ability, delta] of Object.entries(step.adjustments)) {
      totals[ability] = (totals[ability] ?? 0) + delta;
    }
  }
  const summed = Object.entries(totals)
    .map(([ability, delta]) => `${delta > 0 ? '+' : ''}${delta} ${ability}`)
    .join(', ');
  return `${optionLabel(reached[reached.length - 1]!.step)} at ${age}: ${summed}, applied when the character is made.`;
}

/**
 * What a Combat Skill Point costs, from the server's own published prices.
 *
 * The client adds up numbers the server sent; it never prices anything. The
 * total is re-derived on write and the loadout refused there, so this is the
 * courtesy half of the same rule.
 */
export function combatPointsSpent(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): number {
  const catalogue = options?.combatSkills;
  if (!catalogue) return 0;
  let spent = 0;
  for (const skill of draft.weaponSkills) {
    spent += catalogue.tiers.find((tier) => tier.tier === skill.tier)?.cost ?? 0;
  }
  for (const method of draft.combatMethods) {
    spent +=
      catalogue.methods.find((one) => one.name === method.name && one.category === method.category)
        ?.cost ?? 0;
  }
  if (draft.ambidextrous) spent += catalogue.ambidexterity.cost;
  // ⚠ **Read, never guessed.** The surcharge is one point for a melee weapon
  // and two for a bow or crossbow, and which weapon is which is a rule — so
  // the server publishes the figure per weapon. Inferring it from the name
  // would be right for today's table and would show a spend the server refuses
  // the day it stopped being.
  if (draft.specialisedIn) {
    spent += catalogue.specialisationCost?.[draft.specialisedIn] ?? 0;
  }
  return spent;
}

/** What this character has to spend — Table 5.1, as the server published it. */
export function combatPointsAvailable(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): number {
  return options?.classes.find((one) => one.class === draft.characterClass)?.combatSkillPoints ?? 0;
}

/**
 * ⚠ **What is about to be thrown away.**
 *
 * "Initial combat skills represent years of training and practice prior to
 * becoming 1st level, those not exchanged during character creation are lost."
 * Irreversible, invisible, and the reason this step exists at all.
 */
export function combatPointsUnspent(
  draft: CharacterDraft,
  options: CharacterOptions | null,
): number {
  return Math.max(0, combatPointsAvailable(draft, options) - combatPointsSpent(draft, options));
}


/**
 * Which combat methods this character may take, and why not.
 *
 * ⚠ **Three printed sentences that do not compose the way they read.** A
 * single-class fighter may take "any or all" but only one at 1st level;
 * everyone else exactly one, ever; and a single-class wizard one that is
 * *ranged or unarmed*, never melee — which is a **narrowing** of the one-ever
 * rule rather than an exception to it. The server decides; this is what stops a
 * player picking something only to be refused at the end.
 */
export function methodBlockers(
  draft: CharacterDraft,
  options: CharacterOptions | null,
  method: { name: string; category: string },
): string[] {
  const catalogue = options?.combatSkills;
  if (!catalogue) return [];
  const held = draft.combatMethods;
  const already = held.some((one) => one.name === method.name && one.category === method.category);
  if (already) return [];

  const characterClass = draft.characterClass;
  // **A class name, not a rule copied.** The printed sentence names the
  // *fighter* specifically — "single class fighters can choose … any or all" —
  // and a paladin is a warrior who may not, so the class group is the wrong
  // question here.
  const isFighter = characterClass === 'fighter';
  // ⚠ **Which classes are wizards is a rule, so it is READ.** Table 5.1's row
  // name comes from the server with the class; a list written here would be a
  // copy of `classGroups` that goes stale the day a class is added.
  const isWizard =
    options?.classes.find((one) => one.class === characterClass)?.combatSkillGroup === 'wizard';

  if (isWizard && method.category === 'melee') {
    return ['a wizard may never learn a melee combat method'];
  }
  // **One, for everybody, at 1st level** — which is every character this
  // wizard makes. A fighter's "any or all" only opens up as they earn more
  // points, and the two cases differ in what they *say*, not in the number.
  if (held.length >= 1) {
    return [
      isFighter
        ? 'a fighter may choose only one method at 1st level'
        : 'this class may learn one combat method, ever',
    ];
  }
  const spent = combatPointsSpent(draft, options);
  const cost = catalogue.methods.find(
    (one) => one.name === method.name && one.category === method.category,
  )?.cost ?? 0;
  if (spent + cost > combatPointsAvailable(draft, options)) {
    return ['not enough combat skill points left'];
  }
  return [];
}

/**
 * Whether spending `cost` more CSP still fits the budget.
 *
 * ⚠ **`methodBlockers` already asked this before letting a combat method be
 * picked — weapon training, Ambidexterity and specialisation never did.**
 * Ambidexterity could push a 2-CSP class to "3/2 combat skill points spent"
 * with nothing on screen saying why the combat methods below had all gone
 * grey. Those three write straight to the draft with no gate at all, so the
 * wizard let a spend the server would refuse look identical to one it would
 * accept.
 */
export function affordsCsp(
  draft: CharacterDraft,
  options: CharacterOptions | null,
  cost: number,
): boolean {
  return combatPointsSpent(draft, options) + cost <= combatPointsAvailable(draft, options);
}
